import EyeOffNodes from 'lucide-nodes:eye-off'
import type { Map as MapboxMap } from 'mapbox-gl'
import { when } from 'mobx'

import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import { pictogramSlugOf } from '~/client/src/shared/icons/pictograms'
import {
  OUTLINE_ONLY_PLATE,
  PLAIN_LABEL_PLATE,
  SELECTION_RING,
  SELECTION_RING_MARGIN_PX,
} from '~/client/src/shared/map/labelPlateSpec'
import { getZoomScale } from '~/client/src/shared/map/mapZoomRule'
import {
  CLOSED_BADGE_SPEC,
  HAS_CLOSURES_BADGE_SPEC,
  MAP_SHADOW_REACH_PX,
  MARKER_SPEC,
  OFF_VIEW_BADGE_SIZE_PX,
  PICTOGRAM_MARKER_RADIUS,
  PIN_PATH_DATA,
  PIN_SPEC,
  STACK_CHIP_SPEC,
} from '~/client/src/shared/map/markerGeometry'
import ThemeMode from '~/client/src/shared/utils/ThemeModeManager'

import { drawLabelPlate, labelPlateGeometry } from './GLLabelPlateImage'
import {
  drawStackChip,
  STACK_SEGMENT_PATTERN,
  stackedMarkerImageId,
} from './GLStackChipImage'
import {
  compositeMapShadow,
  DEFAULT_GLYPH_STROKE_WIDTH,
  fitViewBox,
  glyphPath2D,
  iconNodesToPath2Ds,
  nodeFill,
  rasterRatio,
  traceRoundRect,
} from './iconGeometry'
import {
  DEFAULT_SORT_KEY,
  getMarkerGlyph,
  ICON_VIEW_BOX,
  IIconData,
  isIconArtLoaded,
  SORT_KEY_BY_TYPE,
} from './markerSpec'

import Colors from '~/client/src/shared/theme.module.scss'

export const MARKER_KEY_PREFIX = 'marker-'
// The project address, which is a bare coordinate rather than a location item: it wears the pin
// silhouette every other map pin wears, not the location circle.
const PROJECT_PIN_KEY_PREFIX = 'projpin-'
const LABEL_PLATE_KEY_PREFIX = 'labelplate'
const SELECTION_OUTLINE_KEY_PREFIX = 'labelselect'
const OFF_VIEW_OUTLINE_KEY_PREFIX = 'offviewselect'

const HIGHLIGHTED_SEGMENT = 'hl'
const CLOSED_SEGMENT = 'cl'
const HAS_CLOSURES_SEGMENT = 'cw'
const LIVE_SEGMENT = 'lv'
// Two-char tokens can't collide with the 6-char hex they follow.
const STATE_SEGMENTS = new Set<string>([
  HIGHLIGHTED_SEGMENT,
  CLOSED_SEGMENT,
  HAS_CLOSURES_SEGMENT,
  LIVE_SEGMENT,
])

export interface IMarkerVisualState {
  iconType: MapViewLocationIcon
  // Resolved hex: theme tokens are hsla() at runtime and canvas needs plain hex.
  color: string
  isHighlighted?: boolean
  isClosed?: boolean
  hasClosures?: boolean
  isLive?: boolean
  stackCount?: number
}

// A sign paints its own colours, so its markers share one image whatever the location colour.
const PICTOGRAM_KEY_COLOR = '000000'

// State-encoded image id (cf. getWorkflowPinImageKey): everything the renderer
// needs parses back out, so images register lazily via styleimagemissing.
export function getMarkerImageKey(state: IMarkerVisualState): string {
  const segments = [
    `${MARKER_KEY_PREFIX}${state.iconType}`,
    pictogramSlugOf(state.iconType)
      ? PICTOGRAM_KEY_COLOR
      : state.color.replace('#', ''),
  ]
  if (state.isHighlighted) segments.push(HIGHLIGHTED_SEGMENT)
  if (state.isClosed) segments.push(CLOSED_SEGMENT)
  if (state.hasClosures) segments.push(HAS_CLOSURES_SEGMENT)
  if (state.isLive) segments.push(LIVE_SEGMENT)
  const key = segments.join('-')
  return state.stackCount ? stackedMarkerImageId(key, state.stackCount) : key
}

// Icon names contain dashes, so the fixed segments parse from the end.
export function parseMarkerImageKey(
  imageId: string,
): IMarkerVisualState | null {
  const parts = imageId.slice(MARKER_KEY_PREFIX.length).split('-')
  let isHighlighted = false
  let isClosed = false
  let hasClosures = false
  let isLive = false
  let stackCount: number | undefined

  const stack = STACK_SEGMENT_PATTERN.exec(parts[parts.length - 1] ?? '')
  if (stack) {
    stackCount = Number(stack[1])
    parts.pop()
  }

  while (parts.length && STATE_SEGMENTS.has(parts[parts.length - 1])) {
    switch (parts.pop()) {
      case HIGHLIGHTED_SEGMENT:
        isHighlighted = true
        break
      case CLOSED_SEGMENT:
        isClosed = true
        break
      case HAS_CLOSURES_SEGMENT:
        hasClosures = true
        break
      case LIVE_SEGMENT:
        isLive = true
        break
    }
  }

  if (parts.length < 2) {
    return null
  }

  const color = '#' + parts.pop()
  return {
    iconType: parts.join('-') as MapViewLocationIcon,
    color,
    isHighlighted,
    isClosed,
    hasClosures,
    isLive,
    ...(stackCount && { stackCount }),
  }
}

export function getSortKey(iconType: MapViewLocationIcon): number {
  return SORT_KEY_BY_TYPE[iconType] ?? DEFAULT_SORT_KEY
}

// Separates an image id from the zoom band it was baked for.
const BAND_SEPARATOR = '-b'

// A plate carries no colour — the border it replaces was the only thing the label's colour
// reached — so unlike the box's id, this one holds nothing but the zoom band. Bandless for a
// tier whose type does not ride a zoom ramp.
export function labelPlateImageId(band: number | null): string {
  return band === null
    ? LABEL_PLATE_KEY_PREFIX
    : `${LABEL_PLATE_KEY_PREFIX}${BAND_SEPARATOR}${band}`
}

// The ring drawn around a SELECTED item — the circle and its label together, not the label
// alone. It is the plate's own 9-patch with nothing in it and a stroke on it, so the box comes
// from icon-text-fit against the same text rather than from a second geometry.
//
// The fixed-width floor is what lets it span the circle around a short label: fixed 9-patch
// columns are emitted at constant pixel size however narrow the text is.

export function selectionOutlineImageId(band: number | null): string {
  return band === null
    ? SELECTION_OUTLINE_KEY_PREFIX
    : `${SELECTION_OUTLINE_KEY_PREFIX}${BAND_SEPARATOR}${band}`
}

// The same ring with an eye-off badge on its top-right corner, for a selection the view hides.
// On the ring rather than the circle, because every selected item has a ring.
export function offViewSelectionOutlineImageId(band: number | null): string {
  return band === null
    ? OFF_VIEW_OUTLINE_KEY_PREFIX
    : `${OFF_VIEW_OUTLINE_KEY_PREFIX}${BAND_SEPARATOR}${band}`
}

// Lucide strokes reach the edge of their grid, so the glyph sits inside the disc.
const OFF_VIEW_GLYPH_RATIO = 0.75

function drawOffViewBadge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  lineWidth: number,
  ringColor: string,
): void {
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
  ctx.fillStyle = MARKER_SPEC.borderColor
  ctx.fill()
  ctx.lineWidth = lineWidth
  ctx.strokeStyle = ringColor
  ctx.stroke()
  drawCenteredIcon(
    ctx,
    { nodes: EyeOffNodes, viewBox: ICON_VIEW_BOX },
    radius * 2 * OFF_VIEW_GLYPH_RATIO,
    x,
    y,
    Colors.mapLabelMutedOnLight,
  )
}

function addSelectionOutlineImage(
  map: MapboxMap,
  imageId: string,
  prefix: string,
): void {
  const isOffView = prefix === OFF_VIEW_OUTLINE_KEY_PREFIX
  const band = imageId.slice(prefix.length + BAND_SEPARATOR.length)
  const ratio = window.devicePixelRatio || 1
  const edgeScale = band ? getZoomScale(Number(band) + 1) : 1
  // The fixed margin has to cover the whole painted stroke, casing included, or the part that
  // does not fit is stretched down the label as a translucent band.
  const strokeTotalPx = SELECTION_RING.widthPx + SELECTION_RING.casingPx
  const geometry = labelPlateGeometry(
    ratio,
    OUTLINE_ONLY_PLATE,
    edgeScale,
    MARKER_SPEC.outerSize,
    strokeTotalPx,
  )
  const ringColor = ThemeMode.getHEXColor(Colors.primary60)
  const { stretchX, stretchY, content } = geometry.metadata
  // A 9-patch stretches its bands, so the badge has to sit wholly on fixed material, a pixel
  // clear of any band so its antialiasing is not smeared. It is centred on the ring's corner:
  // margin outside the content box takes its outer half, and fixed rows added just inside the
  // content's top take its lower half. Fixed rows inside the content keep their size while the
  // band gives up the same amount, so the ring still fits the text exactly.
  const badgeRadius = isOffView
    ? Math.round((OFF_VIEW_BADGE_SIZE_PX / 2) * edgeScale * ratio)
    : 0
  const clearance = isOffView ? Math.ceil(ratio) : 0
  const ringInset = Math.round(
    (SELECTION_RING.casingPx + SELECTION_RING.widthPx / 2) * edgeScale * ratio,
  )
  const top = Math.max(0, badgeRadius - ringInset)
  const contentRows = Math.max(
    0,
    ringInset + badgeRadius + clearance - stretchY[0][0],
  )
  const badgeX = Math.max(
    geometry.width - ringInset,
    stretchX[stretchX.length - 1][1] + badgeRadius + clearance,
  )
  const badgeY = top + ringInset
  const width = isOffView
    ? Math.max(geometry.width, badgeX + badgeRadius)
    : geometry.width
  const ringHeight = geometry.height + contentRows
  const height = ringHeight + top

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return
  }
  ctx.translate(0, top)
  drawLabelPlate(ctx, { ...geometry, height: ringHeight }, OUTLINE_ONLY_PLATE, {
    ...SELECTION_RING,
    color: ringColor,
    casingColor: Colors.mapHaloOnLight,
  })
  ctx.setTransform(1, 0, 0, 1, 0, 0)

  if (isOffView) {
    drawOffViewBadge(
      ctx,
      badgeX,
      badgeY,
      badgeRadius,
      MARKER_SPEC.borderWidth * edgeScale * ratio,
      ringColor,
    )
  }

  const shift = top + contentRows
  map.addImage(
    imageId,
    { width, height, data: ctx.getImageData(0, 0, width, height).data },
    {
      ...geometry.metadata,
      stretchY: stretchY.map(([from, to]) => [from + shift, to + shift]),
      content: [content[0], content[1] + top, content[2], content[3] + shift],
    },
  )
}

// The ring around a selected marker that shows no label. There is no text to fit it to, so it is
// drawn at its final size and rides the marker's own icon-size: the marker's box plus the ring
// margin on every side, whatever shape the marker is.
export const MARKER_SELECTION_RING_IMAGE_ID = 'markerselect'
// The same ring with the eye-off badge on its corner. The image grows by the badge's overhang on
// every side, not just the badged ones, because the layer anchors it by its centre.
export const OFF_VIEW_MARKER_SELECTION_RING_IMAGE_ID = 'offviewmarkerselect'

function addMarkerSelectionRingImage(map: MapboxMap, imageId: string): void {
  const isOffView = imageId === OFF_VIEW_MARKER_SELECTION_RING_IMAGE_ID
  const ratio = window.devicePixelRatio || 1
  const ringColor = ThemeMode.getHEXColor(Colors.primary60)
  const side = Math.round(
    (MARKER_SPEC.outerSize + SELECTION_RING_MARGIN_PX * 2) * ratio,
  )
  const ringInset = Math.round(
    (SELECTION_RING.casingPx + SELECTION_RING.widthPx / 2) * ratio,
  )
  const badgeRadius = isOffView
    ? Math.round((OFF_VIEW_BADGE_SIZE_PX / 2) * ratio)
    : 0
  const pad = Math.max(0, badgeRadius - ringInset)
  const size = side + pad * 2
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return
  }
  ctx.translate(pad, pad)
  drawLabelPlate(
    ctx,
    { width: side, height: side, radius: OUTLINE_ONLY_PLATE.radiusPx * ratio },
    OUTLINE_ONLY_PLATE,
    {
      ...SELECTION_RING,
      color: ringColor,
      casingColor: Colors.mapHaloOnLight,
    },
  )
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  if (isOffView) {
    drawOffViewBadge(
      ctx,
      pad + side - ringInset,
      pad + ringInset,
      badgeRadius,
      MARKER_SPEC.borderWidth * ratio,
      ringColor,
    )
  }
  map.addImage(imageId, ctx.getImageData(0, 0, size, size), {
    pixelRatio: ratio,
  })
}

function addLabelPlateImage(map: MapboxMap, imageId: string): void {
  const band = imageId.slice(
    LABEL_PLATE_KEY_PREFIX.length + BAND_SEPARATOR.length,
  )
  const ratio = window.devicePixelRatio || 1
  // The band the tile was laid out in, so the radius bakes pre-divided by its factor. The
  // plate is fitted from text shaped one level ahead of the tile, which is the level to
  // divide by — the same reasoning the box's border carried.
  const edgeScale = band ? getZoomScale(Number(band) + 1) : 1
  const geometry = labelPlateGeometry(ratio, PLAIN_LABEL_PLATE, edgeScale)
  const { width, height } = geometry

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return
  }
  drawLabelPlate(ctx, geometry, PLAIN_LABEL_PLATE)

  map.addImage(
    imageId,
    { width, height, data: ctx.getImageData(0, 0, width, height).data },
    geometry.metadata,
  )
}

// Legacy glyphs are filled silhouettes; the design-system set is stroked outlines.
function drawCenteredIcon(
  ctx: CanvasRenderingContext2D,
  icon: Pick<IIconData, 'nodes' | 'viewBox'>,
  boxPx: number,
  centerX: number,
  centerY: number,
  color: string,
): void {
  const { scale, x, y } = fitViewBox(icon.viewBox, boxPx, centerX, centerY)

  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)

  ctx.strokeStyle = color
  ctx.lineWidth = DEFAULT_GLYPH_STROKE_WIDTH
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  // Matches how the DOM paints the same nodes: a node that carries a fill is filled, and
  // everything else is stroked — a single glyph can do both.
  const paths = iconNodesToPath2Ds(icon.nodes)
  icon.nodes.forEach(([, attrs], index) => {
    const fill = attrs.fill
    if (fill && fill !== 'none') {
      ctx.fillStyle = nodeFill(fill, color)
      ctx.fill(
        paths[index],
        attrs.fillRule === 'evenodd' ? 'evenodd' : 'nonzero',
      )
    } else {
      ctx.stroke(paths[index])
    }
  })

  ctx.restore()
}

// Badges are one filled path each, not location glyphs, so they don't travel through
// ICON_DATA or its node model.
function drawCenteredBadge(
  ctx: CanvasRenderingContext2D,
  badge: { viewBox: string; path: string },
  boxPx: number,
  centerX: number,
  centerY: number,
  color: string,
): void {
  const { scale, x, y } = fitViewBox(badge.viewBox, boxPx, centerX, centerY)

  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)
  ctx.fillStyle = color
  ctx.fill(glyphPath2D(badge.path), 'evenodd')
  ctx.restore()
}

// Badges overrun the circle, so the canvas grows symmetrically — the circle must
// stay centred on the coordinate because the layer anchors icons by their centre.
function overhangPx(state: IMarkerVisualState, isPictogram: boolean): number {
  const closedSize = state.isHighlighted
    ? CLOSED_BADGE_SPEC.highlightedSize
    : CLOSED_BADGE_SPEC.size
  const closed = state.isClosed ? (closedSize - MARKER_SPEC.outerSize) / 2 : 0
  const closures = state.hasClosures
    ? HAS_CLOSURES_BADGE_SPEC.offsetLeft +
      HAS_CLOSURES_BADGE_SPEC.size +
      // the badge's white ring overhangs the glyph
      MARKER_SPEC.borderWidth -
      MARKER_SPEC.outerSize
    : 0
  const ring = state.isLive ? MARKER_SPEC.liveRingWidth : 0
  const pictogramRing =
    isPictogram && state.isHighlighted ? MARKER_SPEC.highlightedBorderWidth : 0
  const chip = state.stackCount
    ? STACK_CHIP_SPEC.offsetPx +
      STACK_CHIP_SPEC.size / 2 -
      MARKER_SPEC.outerSize / 2
    : 0
  return Math.max(
    0,
    Math.ceil(Math.max(closed, closures, ring, pictogramRing, chip)),
  )
}

// The art is the marker: it fills the circle's box, so the footprint every collision and
// clearance rule measures is unchanged. Highlight rings it from outside rather than shrinking it.
function drawPictogramMarker(
  ctx: CanvasRenderingContext2D,
  icon: IIconData,
  center: number,
  ratio: number,
  isHighlighted: boolean,
): void {
  const size = MARKER_SPEC.outerSize * ratio
  if (isHighlighted) {
    const ring = MARKER_SPEC.highlightedBorderWidth * ratio
    traceRoundRect(
      ctx,
      center - size / 2 - ring,
      center - size / 2 - ring,
      size + ring * 2,
      size + ring * 2,
      PICTOGRAM_MARKER_RADIUS * ratio + ring,
    )
    ctx.fillStyle = MARKER_SPEC.borderColor
    ctx.fill()
  }
  drawCenteredIcon(ctx, icon, size, center, center, MARKER_SPEC.iconColor)
}

function renderMarkerImage(
  state: IMarkerVisualState,
  ratio: number,
): ImageData | null {
  const iconData = getMarkerGlyph(state.iconType)

  const pad = overhangPx(state, !!iconData.isPictogram)
  const canvasPx = Math.round((MARKER_SPEC.outerSize + pad * 2) * ratio)
  const borderWidth =
    (state.isHighlighted
      ? MARKER_SPEC.highlightedBorderWidth
      : MARKER_SPEC.borderWidth) * ratio

  const canvas = document.createElement('canvas')
  canvas.width = canvasPx
  canvas.height = canvasPx
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return null
  }

  const center = canvasPx / 2
  const fillRadius = (MARKER_SPEC.outerSize * ratio) / 2 - borderWidth / 2

  if (state.isLive) {
    ctx.beginPath()
    ctx.arc(
      center,
      center,
      (MARKER_SPEC.outerSize / 2 + MARKER_SPEC.liveRingWidth) * ratio,
      0,
      Math.PI * 2,
    )
    ctx.fillStyle = Colors.mapTeal
    ctx.fill()
  }

  if (iconData.isPictogram) {
    drawPictogramMarker(ctx, iconData, center, ratio, state.isHighlighted)
  } else {
    ctx.beginPath()
    ctx.arc(center, center, fillRadius, 0, Math.PI * 2)
    ctx.fillStyle = state.color
    ctx.fill()
    ctx.lineWidth = borderWidth
    ctx.strokeStyle = MARKER_SPEC.borderColor
    ctx.stroke()

    drawCenteredIcon(
      ctx,
      iconData,
      MARKER_SPEC.iconSize * ratio,
      center,
      center,
      MARKER_SPEC.iconColor,
    )
  }

  if (state.isClosed) {
    const size = state.isHighlighted
      ? CLOSED_BADGE_SPEC.highlightedSize
      : CLOSED_BADGE_SPEC.size
    drawCenteredBadge(
      ctx,
      CLOSED_BADGE_SPEC,
      size * ratio,
      center,
      center,
      ThemeMode.getHEXColor(Colors.error50),
    )
  }

  if (state.hasClosures) {
    const { size, offsetLeft, offsetBottom } = HAS_CLOSURES_BADGE_SPEC
    // Anchored by its left/bottom edges inside the marker box, as the DOM rule does.
    const markerLeft = pad * ratio
    const markerBottom = (pad + MARKER_SPEC.outerSize) * ratio
    const badgeCenterX = markerLeft + (offsetLeft + size / 2) * ratio
    const badgeCenterY = markerBottom - (offsetBottom + size / 2) * ratio

    // The DOM badge is a 16px box with a 1px white border AND a white background, and
    // box-sizing is content-box, so it paints an 18px disc with the glyph filling the
    // inner 16px — a 1px ring that separates the badge from the marker beneath. Drawing
    // the disc at the glyph's own size hides it entirely.
    ctx.beginPath()
    ctx.arc(
      badgeCenterX,
      badgeCenterY,
      (size / 2 + MARKER_SPEC.borderWidth) * ratio,
      0,
      Math.PI * 2,
    )
    ctx.fillStyle = MARKER_SPEC.borderColor
    ctx.fill()

    drawCenteredBadge(
      ctx,
      HAS_CLOSURES_BADGE_SPEC,
      size * ratio,
      badgeCenterX,
      badgeCenterY,
      ThemeMode.getHEXColor(Colors.warning50),
    )
  }

  if (state.stackCount) {
    drawStackChip(
      ctx,
      state.stackCount,
      center - STACK_CHIP_SPEC.offsetPx * ratio,
      center - STACK_CHIP_SPEC.offsetPx * ratio,
      ratio,
    )
  }

  return ctx.getImageData(0, 0, canvasPx, canvasPx)
}

// Idempotent, and synchronous so the symbol places in the same frame that
// requested the image — callers may also use it to warm a variant up front.
/** The project address pin, by colour. One image, and the id carries everything that varies. */
export function getProjectPinImageKey(color: string): string {
  return `${PROJECT_PIN_KEY_PREFIX}${color.replace('#', '')}`
}

// Same silhouette as every workflow pin — the path and its measurements come from the one
// geometry module, so the project pin cannot drift from the rest of the map's pins.
function renderProjectPinImage(color: string): ImageData | null {
  const ratio = window.devicePixelRatio || 1
  const scale = PIN_SPEC.boxPx / PIN_SPEC.viewBoxHeight
  const pinHeight = PIN_SPEC.tipY * scale
  const w = Math.round(
    (Math.ceil(PIN_SPEC.viewBoxWidth * scale) + MAP_SHADOW_REACH_PX.side * 2) *
      ratio,
  )
  const h = Math.round((pinHeight + MAP_SHADOW_REACH_PX.top) * ratio)

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  const art = document.createElement('canvas')
  art.width = w
  art.height = h
  const artCtx = art.getContext('2d')
  if (!ctx || !artCtx) {
    return null
  }

  const centerX = w / 2
  // Pin tip sits exactly at the bottom edge — the layer anchors it there.
  const pinTop = h - pinHeight * ratio

  artCtx.save()
  artCtx.translate(
    centerX - (PIN_SPEC.viewBoxWidth / 2) * scale * ratio,
    pinTop,
  )
  artCtx.scale(scale * ratio, scale * ratio)
  const pin = new Path2D(PIN_PATH_DATA)
  artCtx.fillStyle = color
  artCtx.fill(pin, 'evenodd')
  // Pre-divided by the scale: the stroke weight means CSS pixels, not viewBox units.
  artCtx.lineWidth = PIN_SPEC.strokePx / scale
  artCtx.strokeStyle = Colors.mapWhite
  artCtx.stroke(pin)
  artCtx.restore()

  drawCenteredIcon(
    artCtx,
    getMarkerGlyph(MapViewLocationIcon.Project),
    PIN_SPEC.boxPx * PIN_SPEC.glyphRatio * ratio,
    centerX,
    pinTop + PIN_SPEC.glyphCenterY * scale * ratio,
    Colors.mapWhite,
  )

  compositeMapShadow(ctx, art, w, h, ratio)

  return ctx.getImageData(0, 0, w, h)
}

// styleimagemissing re-fires every frame, so a failing render is a per-frame allocation loop.
const failedProjectPinAttempts = new Map<string, number>()
const MAX_PROJECT_PIN_ATTEMPTS = 3

function addProjectPinImage(map: MapboxMap, imageId: string): void {
  if (
    (failedProjectPinAttempts.get(imageId) ?? 0) >= MAX_PROJECT_PIN_ATTEMPTS
  ) {
    return
  }
  const color = `#${imageId.slice(PROJECT_PIN_KEY_PREFIX.length)}`
  const imageData = renderProjectPinImage(color)
  if (!imageData) {
    failedProjectPinAttempts.set(
      imageId,
      (failedProjectPinAttempts.get(imageId) ?? 0) + 1,
    )
    return
  }
  failedProjectPinAttempts.delete(imageId)
  map.addImage(imageId, imageData, {
    pixelRatio: window.devicePixelRatio || 1,
  })
}

// A sign's or library glyph's marker goes up with its stand-in while the art loads (a sign's tone
// tile, a glyph's empty disc), then is swapped in place, at the
// same size, so nothing re-lays out. A size change (the window moved to another screen) removes it
// instead, and styleimagemissing draws it afresh. Waiting reads the art cache, so a failed load is
// retried here like everywhere else.
// Each pending swap's disposer, per map: removing the map drops them, so a slow or failed load
// doesn't keep a removed map (and its GL context) reachable.
const pendingArtSwaps = new WeakMap<MapboxMap, Map<string, () => void>>()
const removedMaps = new WeakSet<MapboxMap>()
const ART_SWAP_TIMEOUT_MS = 10 * 60_000

function pendingSwapsOf(map: MapboxMap): Map<string, () => void> {
  let swaps = pendingArtSwaps.get(map)
  if (!swaps) {
    const created = new Map<string, () => void>()
    map.once('remove', () => {
      removedMaps.add(map)
      created.forEach(dispose => dispose())
      created.clear()
    })
    pendingArtSwaps.set(map, created)
    swaps = created
  }
  return swaps
}

function swapInArtWhenLoaded(
  map: MapboxMap,
  imageId: string,
  state: IMarkerVisualState,
  placeholder: Pick<ImageData, 'width' | 'height'>,
  density: number,
): void {
  if (removedMaps.has(map)) {
    return
  }
  const waiting = pendingSwapsOf(map)
  if (waiting.has(imageId)) {
    return
  }

  let isSettled = false
  const settle = () => {
    isSettled = true
    waiting.delete(imageId)
  }
  const dispose = when(
    () => isIconArtLoaded(state.iconType),
    () => {
      settle()
      try {
        if (!map.hasImage(imageId)) {
          return
        }
        const imageData = renderMarkerImage(state, rasterRatio(density))
        if (!imageData) {
          return
        }
        if (
          imageData.width === placeholder.width &&
          imageData.height === placeholder.height
        ) {
          map.updateImage(imageId, imageData)
        } else {
          map.removeImage(imageId)
        }
        map.triggerRepaint()
      } catch {
        // The style is reloading or the map is gone: styleimagemissing redraws what is still shown.
      }
    },
    { timeout: ART_SWAP_TIMEOUT_MS, onError: settle },
  )
  if (!isSettled) {
    waiting.set(imageId, dispose)
  }
}

// A stack chip's count is canvas text, so one drawn before Inter loaded is redrawn once it has.
export function redrawStackedMarkerImage(
  map: MapboxMap,
  imageId: string,
  density = 1,
): void {
  // Replaced rather than updated: updateImage refuses a raster a device-pixel-ratio change resized.
  if (map.hasImage(imageId)) {
    map.removeImage(imageId)
  }
  renderMissingMarkerImage(map, imageId, density)
}

// `density`: how many times its own size the map draws a Location icon, so it is rasterized that
// sharp.
export function renderMissingMarkerImage(
  map: MapboxMap,
  imageId: string,
  density = 1,
): void {
  if (map.hasImage(imageId)) {
    return
  }

  if (imageId.startsWith(SELECTION_OUTLINE_KEY_PREFIX)) {
    addSelectionOutlineImage(map, imageId, SELECTION_OUTLINE_KEY_PREFIX)
    return
  }

  if (imageId.startsWith(OFF_VIEW_OUTLINE_KEY_PREFIX)) {
    addSelectionOutlineImage(map, imageId, OFF_VIEW_OUTLINE_KEY_PREFIX)
    return
  }

  if (
    imageId === MARKER_SELECTION_RING_IMAGE_ID ||
    imageId === OFF_VIEW_MARKER_SELECTION_RING_IMAGE_ID
  ) {
    addMarkerSelectionRingImage(map, imageId)
    return
  }

  if (imageId.startsWith(LABEL_PLATE_KEY_PREFIX)) {
    addLabelPlateImage(map, imageId)
    return
  }

  if (imageId.startsWith(PROJECT_PIN_KEY_PREFIX)) {
    addProjectPinImage(map, imageId)
    return
  }

  if (!imageId.startsWith(MARKER_KEY_PREFIX)) {
    return
  }

  const state = parseMarkerImageKey(imageId)
  if (!state) {
    return
  }

  const ratio = rasterRatio(density)
  const imageData = renderMarkerImage(state, ratio)
  if (!imageData) {
    return
  }

  map.addImage(imageId, imageData, { pixelRatio: ratio })

  if (!isIconArtLoaded(state.iconType)) {
    // Only the size is kept, so a pending swap doesn't hold the placeholder's pixels.
    swapInArtWhenLoaded(
      map,
      imageId,
      state,
      {
        width: imageData.width,
        height: imageData.height,
      },
      density,
    )
  }
}
